/**
 * Talari Mail: the whole customer experience, which is three things.
 *
 * A domain is added and its DNS records published, and Woyra verifies them.
 * Mailboxes are paid for a month at a time from the Birr wallet. Mailboxes
 * are created and their credentials handed over. Reading and sending happen
 * in Woyra Mail, so there is no inbox here: the page is an onboarding flow
 * and an administration console, and it is built so that a business buyer
 * who has never published a DNS record can finish it without a phone call.
 *
 * The rules that decide what the page says live in `lib/mailSetup` where
 * they can be tested; this file is the rendering and the requests.
 */

import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { IconCheck, IconExternal, IconInbox } from '../components/icons'
import { LoadingOrb } from '../components/LoadingOrb'
import RequestError from '../components/RequestError'
import { Button, ConfirmDialog, CopyButton, EmptyState, Field, Pill } from '../components/ui'
import { api, ApiError } from '../lib/api'
import { calendarDate } from '../lib/format'
import {
  deriveSteps,
  domainStatusLabel,
  etb,
  etbBalance,
  isLocalPart,
  isVerifiedDomain,
  mailboxStatusLabel,
  monthlyPrice,
  recordKindLabel,
  shortfall,
  splitAddresses,
  topUpPath,
  type SetupStep,
} from '../lib/mailSetup'
import { useToast } from '../lib/toast'
import type { MailAlias, Mailbox, MailboxCredentials, MailDomain, MailPricing, MailPlan, Wallet } from '../lib/types'
import { useApi } from '../lib/useApi'

/* -------------------------------------------------------------------------
   Data
   ------------------------------------------------------------------------- */

interface Estate {
  domains: MailDomain[]
  mailboxes: Mailbox[]
  aliases: MailAlias[]
  /** Aliases are secondary. A failure there is reported on their card and
   *  must not stop a customer from publishing DNS or creating a mailbox. */
  aliasError: string | null
}

async function loadEstate(): Promise<Estate> {
  const domains = await api.mailDomains()
  const verified = domains.filter(isVerifiedDomain)
  const [lists, aliasResults] = await Promise.all([
    Promise.all(domains.map(domain => api.mailboxes(domain.id))),
    Promise.allSettled(verified.map(domain => api.mailAliases(domain.id))),
  ])
  const failed = aliasResults.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  )
  return {
    domains,
    mailboxes: lists.flat().filter(box => box.status !== 'deleted'),
    aliases: aliasResults.flatMap(result => (result.status === 'fulfilled' ? result.value : [])),
    aliasError: failed ? errorText(failed.reason, 'The aliases could not be loaded.') : null,
  }
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

/** The URL is a credential and arrives after a round trip. A tab opened after
 *  an await is exactly what popup blockers exist to stop, so the tab is
 *  opened on the click and pointed at the URL once it arrives. */
async function openMailboxTab(mailboxId: string): Promise<void> {
  const tab = window.open('', '_blank')
  try {
    const { url } = await api.openMailbox(mailboxId)
    if (tab) {
      tab.opener = null
      tab.location.href = url
    } else {
      window.location.assign(url)
    }
  } catch (error) {
    tab?.close()
    throw error
  }
}

/** What the credentials panel shows: the mailbox it belongs to, so "Open
 *  mailbox now" knows which one, and why it is being shown. */
interface CredentialsView {
  mailboxId: string
  title: string
  credentials: MailboxCredentials
}

/* -------------------------------------------------------------------------
   Page
   ------------------------------------------------------------------------- */

export default function Mail() {
  const estate = useApi(loadEstate, [])
  const pricing = useApi(() => api.mailPricing(), [])
  const wallet = useApi(() => api.wallet(), [], 30_000)
  const [credentials, setCredentials] = useState<CredentialsView | null>(null)

  const reloadEstate = estate.reload
  const reloadWallet = wallet.reload
  // Creating a mailbox moves money, so the wallet is re-read with the estate;
  // the rail must never show a balance the server has already spent.
  const changed = useCallback(() => {
    reloadEstate()
    reloadWallet()
  }, [reloadEstate, reloadWallet])

  const data = estate.data
  const signInUrl =
    pricing.data?.sign_in_url ?? data?.mailboxes.find(box => box.sign_in_url)?.sign_in_url ?? null

  const head = (
    <header className="page-head">
      <div className="titles">
        <h1>Mail</h1>
        <p className="subtitle">Business email on your own domain, read in Woyra Mail.</p>
      </div>
      {signInUrl && (
        <a className="btn" href={signInUrl} target="_blank" rel="noreferrer">
          Woyra Mail
          <IconExternal size={14} className="" />
        </a>
      )}
    </header>
  )

  if (!data) {
    return (
      <>
        {head}
        {estate.error ? (
          <RequestError message={estate.error} retry={estate.reload} />
        ) : (
          <div className="mail-loading">
            <LoadingOrb />
          </div>
        )}
      </>
    )
  }

  const steps = deriveSteps({
    domains: data.domains,
    mailboxes: data.mailboxes,
    wallet: wallet.data,
    pricing: pricing.data,
  })
  const verified = data.domains.filter(isVerifiedDomain)

  return (
    <>
      {head}
      {estate.error && (
        <div className="alert error" role="alert">
          {estate.error}
        </div>
      )}

      <SetupRail
        steps={steps}
        pricing={pricing.data}
        pricingError={pricing.error}
        retryPricing={pricing.reload}
        wallet={wallet.data}
        mailboxes={data.mailboxes}
      />

      <DomainsCard domains={data.domains} onChanged={changed} />

      {verified.length === 0 ? (
        <section className="card card-pad-lg" aria-label="Mailboxes">
          <EmptyState
            icon={<IconInbox className="empty-art" size={46} />}
            title="Mailboxes unlock once a domain is verified"
          >
            Publish the DNS records above and press Verify. Creating the first mailbox takes a
            minute after that, and you will be given its address and password straight away.
          </EmptyState>
        </section>
      ) : (
        verified.map(domain => {
          const own = data.mailboxes.filter(box => box.domain_id === domain.id)
          return (
            <Fragment key={domain.id}>
              <MailboxesCard
                domain={domain}
                mailboxes={own}
                pricing={pricing.data}
                pricingError={pricing.error}
                retryPricing={pricing.reload}
                wallet={wallet.data}
                onChanged={changed}
                onCredentials={setCredentials}
              />
              <AliasesCard
                domain={domain}
                aliases={data.aliases.filter(alias => alias.domain_id === domain.id)}
                mailboxes={own}
                loadError={data.aliasError}
                onChanged={reloadEstate}
              />
            </Fragment>
          )
        })
      )}

      <GuideCard signInUrl={signInUrl} />

      <CredentialsDialog view={credentials} onClose={() => setCredentials(null)} />
    </>
  )
}

/* -------------------------------------------------------------------------
   The rail
   ------------------------------------------------------------------------- */

function SetupRail({
  steps,
  pricing,
  pricingError,
  retryPricing,
  wallet,
  mailboxes,
}: {
  steps: SetupStep[]
  pricing: MailPricing | null
  pricingError: string | null
  retryPricing: () => void
  wallet: Wallet | null
  mailboxes: Mailbox[]
}) {
  const price = monthlyPrice(pricing)
  const balance = etbBalance(wallet)
  // The amount Billing opens with: what is missing for the next round, or a
  // month for one mailbox when nothing is missing yet.
  const renewing = mailboxes.filter(box => box.status === 'active' || box.suspended_for_billing)
  const due = price == null ? null : renewing.length > 0
    ? renewing.reduce((total, box) => total + Number(box.monthly_etb), 0)
    : price
  const short = due == null ? 0 : shortfall(balance, due)

  return (
    <ol className="mail-steps" aria-label="Setup steps">
      {steps.map((step, index) => (
        <li
          key={step.key}
          className="mail-step"
          data-state={step.state}
          aria-current={step.state === 'current' ? 'step' : undefined}
        >
          <span className="mail-step-number" aria-hidden="true">
            {step.state === 'done' ? <IconCheck size={14} className="" /> : index + 1}
          </span>
          <div className="mail-step-body">
            <strong>{step.title}</strong>
            <span className="mail-step-summary">{step.summary}</span>
            {step.key === 'payment' && (
              <>
                {pricingError ? (
                  <span className="mail-step-actions">
                    <span className="tiny" style={{ color: 'var(--danger)' }}>
                      The price could not be loaded: {pricingError}
                    </span>
                    <Button size="sm" onClick={retryPricing}>
                      Try again
                    </Button>
                  </span>
                ) : (
                  <dl className="mail-price">
                    <dt>Plans per mailbox</dt>
                    <dd>{pricing ? pricing.plans.map(item => `${item.label}: ${etb(item.monthly_etb)}/mo`).join(' · ') : '—'}</dd>
                    <dt>Storage</dt>
                    <dd>{pricing ? `${pricing.included_storage_gb} GB included` : '—'}</dd>
                    <dt>Birr balance</dt>
                    <dd>{wallet ? etb(balance) : '—'}</dd>
                  </dl>
                )}
                <span className="mail-step-actions">
                  <Link className="btn sm primary" to={topUpPath(short > 0 ? short : price ?? 0)}>
                    Top up
                  </Link>
                  <Link className="tiny" to="/billing">
                    Billing
                  </Link>
                </span>
                <span className="mail-step-summary">
                  Charged monthly in advance from your wallet. If the wallet cannot cover a renewal
                  the mailbox is paused with its mail kept, and resumes when you top up.
                </span>
              </>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}

/* -------------------------------------------------------------------------
   Domains
   ------------------------------------------------------------------------- */

function DomainsCard({ domains, onChanged }: { domains: MailDomain[]; onChanged: () => void }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState('')
  const [notice, setNotice] = useState('')
  const [removing, setRemoving] = useState<MailDomain | null>(null)

  async function run(key: string, action: () => Promise<void>, fallback: string) {
    setBusy(key)
    setProblem('')
    setNotice('')
    try {
      await action()
    } catch (error) {
      setProblem(errorText(error, fallback))
    } finally {
      setBusy(null)
    }
  }

  function add(event: FormEvent) {
    event.preventDefault()
    const typed = name.trim()
    if (!typed || busy) return
    void run(
      'add',
      async () => {
        const created = await api.addMailDomain(typed)
        setName('')
        setNotice(`${created.name} added. Publish its DNS records below, then press Verify.`)
        onChanged()
      },
      'The domain could not be added.',
    )
  }

  function verify(domain: MailDomain) {
    void run(
      domain.id,
      async () => {
        const checked = await api.checkMailDomain(domain.id)
        onChanged()
        if (checked.status === 'active') {
          setNotice(`${checked.name} is verified and receiving mail.`)
          return
        }
        if (isVerifiedDomain(checked)) {
          setNotice(
            `${checked.name} is verified. You can create mailboxes now; incoming mail moves to Woyra once the MX record is in place.`,
          )
          return
        }
        const missing = checked.records.filter(record => !record.verified)
        setNotice(
          missing.length === 0
            ? 'Every record is in place. Woyra is still confirming; check again in a few minutes.'
            : `${checked.name} is not verified yet: ${missing.length} ${missing.length === 1 ? 'record is' : 'records are'} still missing. A DNS change can take up to an hour to be seen.`,
        )
      },
      'The domain could not be checked.',
    )
  }

  function remove() {
    if (!removing) return
    const domain = removing
    void run(
      'remove',
      async () => {
        await api.removeMailDomain(domain.id)
        setRemoving(null)
        setNotice(`${domain.name} removed.`)
        onChanged()
      },
      'The domain was not removed.',
    )
  }

  return (
    <section className="card card-pad-lg stack" aria-labelledby="mail-domains-title">
      <div>
        <p className="platform-eyebrow">STEP 1 · DOMAIN</p>
        <h2 id="mail-domains-title">Your domain</h2>
        <p className="muted">
          Addresses live at your own domain, like sara@yourcompany.com. Add it, publish the records
          at your DNS provider, then verify.
        </p>
      </div>

      <form className="mail-add-row" onSubmit={add}>
        <Field
          label="Domain"
          htmlFor="mail-domain-name"
          hint="The bare domain, like yourcompany.com. A leading www. is dropped."
        >
          <input
            id="mail-domain-name"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="yourcompany.com"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            disabled={busy === 'add'}
          />
        </Field>
        <Button type="submit" variant="primary" loading={busy === 'add'} disabled={!name.trim() || !!busy}>
          Add domain
        </Button>
      </form>

      {notice && (
        <p className="alert info" role="status">
          {notice}
        </p>
      )}
      {problem && (
        <p className="alert error" role="alert">
          {problem}
        </p>
      )}

      {domains.length === 0 && (
        <p className="tiny muted">
          No domain yet. Add the one your company uses for its website; the records to publish
          appear here as soon as it is added.
        </p>
      )}

      {domains.map(domain => (
        <DomainRow
          key={domain.id}
          domain={domain}
          busy={busy}
          onVerify={() => verify(domain)}
          onRemove={() => setRemoving(domain)}
        />
      ))}

      <ConfirmDialog
        open={removing !== null}
        title={`Remove ${removing?.name ?? 'this domain'}?`}
        body="The domain leaves Talari Mail and Woyra. Adding it again later means publishing its DNS records afresh. The records at your DNS provider are not touched."
        confirmLabel="Remove domain"
        destructive
        busy={busy === 'remove'}
        onConfirm={remove}
        onCancel={() => {
          if (busy !== 'remove') setRemoving(null)
        }}
      />
    </section>
  )
}

function DomainRow({
  domain,
  busy,
  onVerify,
  onRemove,
}: {
  domain: MailDomain
  busy: string | null
  onVerify: () => void
  onRemove: () => void
}) {
  const status = domainStatusLabel(domain.status)
  const verified = isVerifiedDomain(domain)
  const checkable = domain.status === 'pending' || domain.status === 'verified'

  const table = domain.records.length > 0 && (
    <div className="mail-table-wrap">
      <table className="mail-table mail-records">
        <caption className="sr-only">DNS records for {domain.name}</caption>
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Host</th>
            <th scope="col">Value</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {domain.records.map((record, index) => (
            <tr key={`${record.type}-${record.host}-${index}`}>
              <td>
                <strong>{record.type}</strong>
                <span className="tiny muted mail-record-kind">{recordKindLabel(record.kind)}</span>
              </td>
              <td>
                <code>{record.host}</code>
                <CopyButton value={record.host} label="Copy host" />
              </td>
              <td>
                <code>{record.value}</code>
                {record.priority != null && (
                  <span className="tiny muted mail-record-kind">Priority {record.priority}</span>
                )}
                <CopyButton value={record.value} label="Copy value" />
              </td>
              <td>
                <Pill tone={record.verified ? 'ok' : 'warn'}>{record.verified ? 'Found' : 'Required'}</Pill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <article className="mail-domain" aria-label={`Domain ${domain.name}`}>
      <div className="mail-domain-head">
        <h3>{domain.name}</h3>
        <Pill tone={status.tone}>{status.label}</Pill>
      </div>

      {domain.status === 'failed' && (
        <p className="alert error" role="alert">
          {domain.last_error ?? 'The mail service did not accept this domain.'} Remove it and add it
          again.
        </p>
      )}

      {domain.status !== 'active' && domain.records.length > 0 && (
        <p className="tiny muted">
          The ownership, SPF and DKIM records let {domain.name} send mail. MX is the record that
          moves incoming mail to Woyra: until you change it, mail sent to your addresses keeps going
          wherever it goes today, so it can be the last record you publish, once the first mailbox
          is ready.
        </p>
      )}

      {domain.status === 'active' ? (
        <details>
          <summary>DNS records</summary>
          {table}
        </details>
      ) : (
        table
      )}

      <div className="mail-domain-actions">
        {checkable && (
          <Button
            variant={verified ? 'secondary' : 'primary'}
            disabled={!!busy}
            loading={busy === domain.id}
            onClick={onVerify}
          >
            {verified ? 'Check again' : 'Verify'}
          </Button>
        )}
        {domain.verified_at && <span className="tiny muted">Verified {calendarDate(domain.verified_at)}</span>}
        <Button size="sm" variant="danger" disabled={!!busy || domain.mailbox_count > 0} onClick={onRemove}>
          Remove
        </Button>
      </div>
      {domain.mailbox_count > 0 && (
        <p className="tiny faint">
          To remove this domain, delete its {domain.mailbox_count}{' '}
          {domain.mailbox_count === 1 ? 'mailbox' : 'mailboxes'} first.
        </p>
      )}
    </article>
  )
}

/* -------------------------------------------------------------------------
   Mailboxes
   ------------------------------------------------------------------------- */

type Confirmation = { kind: 'delete' | 'reset'; box: Mailbox }

function MailboxesCard({
  domain,
  mailboxes,
  pricing,
  pricingError,
  retryPricing,
  wallet,
  onChanged,
  onCredentials,
}: {
  domain: MailDomain
  mailboxes: Mailbox[]
  pricing: MailPricing | null
  pricingError: string | null
  retryPricing: () => void
  wallet: Wallet | null
  onChanged: () => void
  onCredentials: (view: CredentialsView) => void
}) {
  const [localPart, setLocalPart] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [plan, setPlan] = useState<MailPlan>('standard')
  const [busy, setBusy] = useState<string | null>(null)
  const [createError, setCreateError] = useState<{ message: string; status: number | null } | null>(null)
  // The status travels with the message because a 402 is the one error the
  // customer fixes on another page, and the fix belongs next to the words.
  const [problem, setProblem] = useState<{ message: string; status: number | null } | null>(null)
  const [confirm, setConfirm] = useState<Confirmation | null>(null)
  const { notify } = useToast()

  const price = monthlyPrice(pricing, plan)
  const balance = etbBalance(wallet)
  const short = price == null ? 0 : shortfall(balance, price)
  const valid = isLocalPart(localPart)
  const localId = `mail-local-${domain.id}`

  async function create(event: FormEvent) {
    event.preventDefault()
    if (!valid || price == null || busy) return
    setBusy('create')
    setCreateError(null)
    setProblem(null)
    try {
      const created = await api.createMailbox(domain.id, {
        local_part: localPart.trim().toLowerCase(),
        plan,
        display_name: displayName.trim() || null,
      })
      setLocalPart('')
      setDisplayName('')
      onCredentials({ mailboxId: created.id, title: `${created.address} is ready`, credentials: created.credentials })
      onChanged()
    } catch (error) {
      setCreateError({
        message: errorText(error, 'The mailbox could not be created.'),
        status: error instanceof ApiError ? error.status : null,
      })
    } finally {
      setBusy(null)
    }
  }

  async function act(key: string, action: () => Promise<void>, fallback: string) {
    setBusy(key)
    setProblem(null)
    try {
      await action()
    } catch (error) {
      setProblem({
        message: errorText(error, fallback),
        status: error instanceof ApiError ? error.status : null,
      })
    } finally {
      setBusy(null)
    }
  }

  function open(box: Mailbox) {
    void act(`open-${box.id}`, () => openMailboxTab(box.id), 'The mailbox could not be opened.')
  }

  function setPaused(box: Mailbox, suspended: boolean) {
    // Resuming a mailbox the wallet stopped charges the month it was stopped
    // for before anything else happens; the server refuses with a 402 if the
    // wallet cannot cover it, and the Top up button appears with the error.
    const charged = !suspended && box.suspended_for_billing
    void act(
      `pause-${box.id}`,
      async () => {
        await api.updateMailbox(box.id, { suspended })
        notify(
          suspended
            ? `${box.address} paused. Its mail is kept.`
            : charged
              ? `${box.address} resumed. This month was charged to your wallet.`
              : `${box.address} resumed.`,
        )
        onChanged()
      },
      suspended ? 'The mailbox could not be paused.' : 'The mailbox could not be resumed.',
    )
  }

  function confirmed() {
    if (!confirm) return
    const { kind, box } = confirm
    void act(
      'confirm',
      async () => {
        if (kind === 'delete') {
          await api.deleteMailbox(box.id)
          notify(`${box.address} deleted.`)
        } else {
          const fresh = await api.resetMailboxPassword(box.id)
          onCredentials({ mailboxId: box.id, title: `New password for ${box.address}`, credentials: fresh })
        }
        setConfirm(null)
        onChanged()
      },
      kind === 'delete' ? 'The mailbox was not deleted.' : 'The password was not reset.',
    )
  }

  return (
    <section className="card card-pad-lg stack" aria-labelledby={`mail-mailboxes-${domain.id}`}>
      <div>
        <p className="platform-eyebrow">STEP 3 · MAILBOXES</p>
        <h2 id={`mail-mailboxes-${domain.id}`}>Mailboxes on {domain.name}</h2>
        <p className="muted">
          Each mailbox is one person's address, with its own password
          {pricing ? ` and ${pricing.included_storage_gb} GB of storage` : ''}. The first month is
          charged when it is created; you are given the address and password once, and can reset
          the password here at any time.
        </p>
      </div>

      <form className="stack" onSubmit={create} aria-label={`New mailbox on ${domain.name}`}>
        <Field label="Plan" htmlFor={`mail-plan-${domain.id}`} hint="Per mailbox, per month. Charged from your wallet.">
          <select
            id={`mail-plan-${domain.id}`}
            value={plan}
            onChange={event => setPlan(event.target.value as MailPlan)}
            disabled={!!busy || !pricing}
          >
            {pricing?.plans.map(item => (
              <option key={item.tier} value={item.tier}>
                {item.label} — {etb(item.monthly_etb)}/month
              </option>
            ))}
          </select>
        </Field>
        <div className="mail-create">
          <Field label="Address" htmlFor={localId} hint="Letters, digits, dots, dashes and underscores.">
            <div className="mail-local">
              <input
                id={localId}
                value={localPart}
                onChange={event => setLocalPart(event.target.value)}
                placeholder="sara"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                disabled={busy === 'create'}
                aria-describedby={`${localId}-hint`}
                aria-invalid={localPart.length > 0 && !valid ? true : undefined}
              />
              <span aria-hidden="true">@{domain.name}</span>
            </div>
          </Field>
          <Field
            label="Display name"
            htmlFor={`mail-name-${domain.id}`}
            hint="Shown to the people who receive mail from it. Optional."
          >
            <input
              id={`mail-name-${domain.id}`}
              value={displayName}
              onChange={event => setDisplayName(event.target.value)}
              placeholder="Sara Tesfaye"
              autoComplete="off"
              disabled={busy === 'create'}
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            loading={busy === 'create'}
            disabled={!valid || price == null || !!busy}
          >
            Create mailbox
          </Button>
        </div>

        <p className="mail-charge">
          {pricingError ? (
            <>
              <span style={{ color: 'var(--danger)' }}>The price could not be loaded, so nothing can be created yet.</span>
              <Button size="sm" type="button" onClick={retryPricing}>
                Try again
              </Button>
            </>
          ) : price == null ? (
            <span>Loading the price…</span>
          ) : (
            <>
              <span>
                Charged now: <strong>{etb(price)}</strong> for the first month
              </span>
              <span>
                Birr balance: <strong>{wallet ? etb(balance) : '—'}</strong>
              </span>
              {wallet && short > 0 && (
                <Link className="btn sm" to={topUpPath(short)}>
                  Add {etb(short)}
                </Link>
              )}
            </>
          )}
        </p>

        {createError && (
          <div className="alert error mail-create-error" role="alert">
            <span className="grow">{createError.message}</span>
            {createError.status === 402 && (
              <Link className="btn primary sm" to={topUpPath(short > 0 ? short : price ?? 0)}>
                Top up
              </Link>
            )}
          </div>
        )}
      </form>

      {problem && (
        <div className="alert error mail-create-error" role="alert">
          <span className="grow">{problem.message}</span>
          {problem.status === 402 && (
            <Link className="btn primary sm" to={topUpPath(short > 0 ? short : price ?? 0)}>
              Top up
            </Link>
          )}
        </div>
      )}

      {mailboxes.length === 0 ? (
        <p className="tiny muted">No mailbox on {domain.name} yet.</p>
      ) : (
        <div className="mail-table-wrap">
          <table className="mail-table">
            <caption className="sr-only">Mailboxes on {domain.name}</caption>
            <thead>
              <tr>
                <th scope="col">Address</th>
                <th scope="col">Name</th>
                <th scope="col">Plan</th>
                <th scope="col">Status</th>
                <th scope="col">Paid until</th>
                <th scope="col">Password</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {mailboxes.map(box => {
                const status = mailboxStatusLabel(box)
                const active = box.status === 'active'
                const paused = box.status === 'suspended'
                const boxPrice = Number(box.monthly_etb)
                const boxShort = shortfall(balance, boxPrice)
                return (
                  <tr key={box.id}>
                    <td>
                      <code className="mail-address">{box.address}</code>
                    </td>
                    <td>{box.display_name || <span className="faint">—</span>}</td>
                    <td>{pricing?.plans.find(item => item.tier === box.plan)?.label ?? box.plan} · {etb(box.monthly_etb)}/mo</td>
                    <td>
                      <Pill tone={status.tone}>{status.label}</Pill>
                    </td>
                    <td className="tabular">{box.paid_until ? calendarDate(box.paid_until) : '—'}</td>
                    <td>{box.has_password ? 'Set' : 'Not set'}</td>
                    <td>
                      <div className="mail-actions">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={!active || !!busy}
                          loading={busy === `open-${box.id}`}
                          onClick={() => open(box)}
                        >
                          Open
                        </Button>
                        <Button size="sm" disabled={!active || !!busy} onClick={() => setConfirm({ kind: 'reset', box })}>
                          Reset password
                        </Button>
                        {paused && box.suspended_for_billing ? (
                          // The wallet stopped it, so a month is owed before
                          // it comes back. Offer to pay it when the balance
                          // can, and the way to Billing when it cannot.
                          wallet && Number.isFinite(boxPrice) && boxShort === 0 ? (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={!!busy}
                              loading={busy === `pause-${box.id}`}
                              onClick={() => setPaused(box, false)}
                            >
                              Pay and resume
                            </Button>
                          ) : (
                            <Link className="btn sm primary" to={topUpPath(boxShort > 0 ? boxShort : boxPrice)}>
                              Top up to resume
                            </Link>
                          )
                        ) : paused ? (
                          <Button
                            size="sm"
                            disabled={!!busy}
                            loading={busy === `pause-${box.id}`}
                            onClick={() => setPaused(box, false)}
                          >
                            Resume
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!active || !!busy}
                            loading={busy === `pause-${box.id}`}
                            onClick={() => setPaused(box, true)}
                          >
                            Pause
                          </Button>
                        )}
                        <Button size="sm" variant="danger" disabled={!!busy} onClick={() => setConfirm({ kind: 'delete', box })}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm?.kind === 'delete'
            ? `Delete ${confirm.box.address}?`
            : `Reset the password for ${confirm?.box.address ?? 'this mailbox'}?`
        }
        body={
          confirm?.kind === 'delete'
            ? 'Everything ever received at this address is deleted, and mail sent to it afterwards bounces. The month already paid for is not refunded.'
            : 'A new password is generated and shown to you once. The current password stops working and every session of this mailbox ends, including tabs opened with Open. Open works again straight away.'
        }
        confirmLabel={confirm?.kind === 'delete' ? 'Delete mailbox' : 'Reset password'}
        requireText={confirm?.kind === 'delete' ? confirm.box.address : undefined}
        destructive={confirm?.kind === 'delete'}
        busy={busy === 'confirm'}
        onConfirm={confirmed}
        onCancel={() => {
          if (busy !== 'confirm') setConfirm(null)
        }}
      />
    </section>
  )
}

/* -------------------------------------------------------------------------
   Aliases
   ------------------------------------------------------------------------- */

function AliasesCard({
  domain,
  aliases,
  mailboxes,
  loadError,
  onChanged,
}: {
  domain: MailDomain
  aliases: MailAlias[]
  mailboxes: Mailbox[]
  loadError: string | null
  onChanged: () => void
}) {
  const [localPart, setLocalPart] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [others, setOthers] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [removing, setRemoving] = useState<MailAlias | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState('')
  const { notify } = useToast()

  const addresses = mailboxes.filter(box => box.status !== 'failed').map(box => box.address)
  const destinations = [...new Set([...picked, ...splitAddresses(others)])]
  const valid = isLocalPart(localPart) && destinations.length > 0
  const localId = `mail-alias-${domain.id}`

  function reset() {
    setLocalPart('')
    setPicked([])
    setOthers('')
    setEditing(null)
    setProblem('')
  }

  function repoint(alias: MailAlias) {
    setEditing(alias.local_part)
    setLocalPart(alias.local_part)
    setPicked(alias.destinations.filter(address => addresses.includes(address)))
    setOthers(alias.destinations.filter(address => !addresses.includes(address)).join(', '))
    setProblem('')
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!valid || busy) return
    setBusy('save')
    setProblem('')
    try {
      const saved = await api.setMailAlias(domain.id, localPart.trim().toLowerCase(), destinations)
      notify(
        `${saved.local_part}@${domain.name} delivers to ${saved.destinations.length} ${saved.destinations.length === 1 ? 'address' : 'addresses'}.`,
      )
      reset()
      onChanged()
    } catch (error) {
      setProblem(errorText(error, 'The alias could not be saved.'))
    } finally {
      setBusy(null)
    }
  }

  async function remove() {
    if (!removing) return
    const alias = removing
    setBusy('remove')
    setProblem('')
    try {
      await api.deleteMailAlias(domain.id, alias.local_part)
      notify(`${alias.local_part}@${domain.name} removed.`)
      setRemoving(null)
      if (editing === alias.local_part) reset()
      onChanged()
    } catch (error) {
      setProblem(errorText(error, 'The alias was not removed.'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="card card-pad-lg stack" aria-labelledby={`mail-aliases-${domain.id}`}>
      <div>
        <p className="platform-eyebrow">ALIASES</p>
        <h2 id={`mail-aliases-${domain.id}`}>Shared addresses on {domain.name}</h2>
        <p className="muted">
          An alias like sales@ delivers to one or more mailboxes. It has no mailbox of its own and
          costs nothing, and it outlives the person answering it: repoint it when they move on.
        </p>
      </div>

      {loadError && (
        <p className="alert error" role="alert">
          Aliases could not be loaded: {loadError}
        </p>
      )}

      {aliases.length > 0 && (
        <div className="list">
          {aliases.map(alias => (
            <div className="list-item mail-alias-row" key={alias.id}>
              <div className="grow">
                <code>
                  {alias.local_part}@{domain.name}
                </code>
                <span className="tiny muted">delivers to {alias.destinations.join(', ')}</span>
              </div>
              <Button size="sm" disabled={!!busy} onClick={() => repoint(alias)}>
                Repoint
              </Button>
              <Button size="sm" variant="danger" disabled={!!busy} onClick={() => setRemoving(alias)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      <form
        className="mail-alias-form"
        onSubmit={save}
        aria-label={editing ? `Repoint ${editing}@${domain.name}` : `New alias on ${domain.name}`}
      >
        <Field label={editing ? 'Alias' : 'New alias'} htmlFor={localId}>
          <div className="mail-local">
            <input
              id={localId}
              value={localPart}
              onChange={event => setLocalPart(event.target.value)}
              placeholder="sales"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={editing !== null || busy === 'save'}
            />
            <span aria-hidden="true">@{domain.name}</span>
          </div>
        </Field>

        <fieldset className="field">
          <legend className="label">Deliver to</legend>
          {addresses.length === 0 ? (
            <span className="hint">No mailbox on this domain yet. Type an address below.</span>
          ) : (
            <div className="mail-alias-picks">
              {addresses.map(address => (
                <label className="check" key={address}>
                  <input
                    type="checkbox"
                    checked={picked.includes(address)}
                    disabled={busy === 'save'}
                    onChange={event =>
                      setPicked(current =>
                        event.target.checked ? [...current, address] : current.filter(item => item !== address),
                      )
                    }
                  />
                  <span className="mono">{address}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <Field
          label="Other addresses"
          htmlFor={`${localId}-others`}
          hint="Optional. Separate several with commas; an address outside this domain works too."
        >
          <input
            id={`${localId}-others`}
            value={others}
            onChange={event => setOthers(event.target.value)}
            placeholder="abel@example.com"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            disabled={busy === 'save'}
          />
        </Field>

        <div className="row">
          <Button type="submit" variant="primary" loading={busy === 'save'} disabled={!valid || !!busy}>
            {editing ? 'Save' : 'Add alias'}
          </Button>
          {editing && (
            <Button type="button" variant="ghost" disabled={busy === 'save'} onClick={reset}>
              Cancel
            </Button>
          )}
        </div>

        {problem && (
          <p className="alert error" role="alert">
            {problem}
          </p>
        )}
      </form>

      <ConfirmDialog
        open={removing !== null}
        title={`Remove ${removing?.local_part ?? ''}@${domain.name}?`}
        body="Mail sent to this address will bounce. The mailboxes it delivered to are not affected."
        confirmLabel="Remove alias"
        destructive
        busy={busy === 'remove'}
        onConfirm={() => void remove()}
        onCancel={() => {
          if (busy !== 'remove') setRemoving(null)
        }}
      />
    </section>
  )
}

/* -------------------------------------------------------------------------
   Using your mailbox
   ------------------------------------------------------------------------- */

function GuideCard({ signInUrl }: { signInUrl: string | null }) {
  const host = signInUrl ? signInUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : null
  return (
    <section className="card card-pad-lg stack" aria-labelledby="mail-guide-title">
      <div>
        <p className="platform-eyebrow">USING YOUR MAILBOX</p>
        <h2 id="mail-guide-title">Reading and sending</h2>
      </div>
      <div className="mail-guide-grid">
        <div>
          <h3>On the web</h3>
          <p>
            Mail is read in Woyra Mail
            {signInUrl && host ? (
              <>
                {' '}
                at{' '}
                <a href={signInUrl} target="_blank" rel="noreferrer">
                  {host}
                </a>
              </>
            ) : null}
            . Sign in with the mailbox address and its password, or press Open on this page to go
            straight in without one.
          </p>
        </div>
        <div>
          <h3>For your staff</h3>
          <p>
            Hand each person their address and password. They do not need a TalariCloud account.
            If a password is lost, reset it here; the old one stops working at once.
          </p>
        </div>
        <div>
          <h3>Not yet available</h3>
          <p>
            Outlook, Apple Mail and phone mail apps (IMAP) cannot connect yet, and migration from
            Gmail or Microsoft 365 is not offered yet. Until that changes, mail is read in Woyra
            Mail on the web; this page will say when it does.
          </p>
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------
   Credentials, shown once
   ------------------------------------------------------------------------- */

function CredentialsDialog({ view, onClose }: { view: CredentialsView | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const [opening, setOpening] = useState(false)
  const [problem, setProblem] = useState('')

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (view && !dialog.open) dialog.showModal()
    if (!view && dialog.open) dialog.close()
  }, [view])

  function close() {
    setProblem('')
    onClose()
  }

  async function open() {
    if (!view) return
    setOpening(true)
    setProblem('')
    try {
      await openMailboxTab(view.mailboxId)
    } catch (error) {
      setProblem(errorText(error, 'The mailbox could not be opened.'))
    } finally {
      setOpening(false)
    }
  }

  return (
    <dialog
      ref={ref}
      className="mail-credentials-dialog"
      aria-labelledby="mail-credentials-title"
      // Escape does not close it: the password on screen cannot be shown
      // again, and a reflex keystroke must not be how it is lost.
      onCancel={event => event.preventDefault()}
      onClose={close}
    >
      {view && (
        <div className="sheet">
          <div className="stack" style={{ gap: 8 }}>
            <h2 id="mail-credentials-title">{view.title}</h2>
            <p className="muted">
              Save these now. The password is shown once and never again; you can reset it from
              this page at any time.
            </p>
          </div>

          <dl className="mail-credentials">
            <div>
              <dt>Address</dt>
              <dd>
                <code>{view.credentials.address}</code>
                <CopyButton value={view.credentials.address} label="Copy address" />
              </dd>
            </div>
            <div>
              <dt>Password</dt>
              <dd>
                <code className="mail-password">{view.credentials.password}</code>
                <CopyButton value={view.credentials.password} label="Copy password" />
              </dd>
            </div>
            <div>
              <dt>Sign in at</dt>
              <dd>
                <a href={view.credentials.sign_in_url} target="_blank" rel="noreferrer">
                  {view.credentials.sign_in_url}
                </a>
                <CopyButton value={view.credentials.sign_in_url} label="Copy link" />
              </dd>
            </div>
          </dl>

          {problem && (
            <p className="alert error" role="alert">
              {problem}
            </p>
          )}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={close} disabled={opening}>
              Done
            </Button>
            <Button variant="primary" loading={opening} onClick={() => void open()}>
              Open mailbox now
            </Button>
          </div>
        </div>
      )}
    </dialog>
  )
}


